import { Card } from "@devhop/ui";
import { useNavigate } from "@tanstack/react-router";
import { ChevronRight, Inbox } from "lucide-react";
import { useMemo } from "react";
import { usePermissions } from "@/modules/auth/presentation/model/usePermissions";
import { useExpiringContractsQuery } from "@/modules/contracts/presentation/api/queries";
import { useLeaveRequestsQuery } from "@/modules/hr/leave/presentation/api/queries";
import { useApprovalSummaryQuery } from "@/modules/hr/overtime/presentation/api/queries";
import { useSalaryAdvancesQuery } from "@/modules/hr/salary-advance/presentation/api/queries";
import { Permissions } from "@/modules/roles/domain/contracts/permissions";
import { dashboardLayout } from "../lib/dashboard-layout";
import { DashboardSection } from "./DashboardSection";

type InboxRow = {
  id: string;
  label: string;
  count: number;
  href: string;
};

function InboxList({ items }: { items: InboxRow[] }) {
  const navigate = useNavigate();

  return (
    <ul className="divide-y divide-border rounded-xl border border-border bg-card">
      {items.map((item) => (
        <li key={item.id}>
          <button
            type="button"
            className={`flex w-full items-center justify-between gap-3 text-left transition-colors hover:bg-muted/50 ${dashboardLayout.barPad}`}
            onClick={() => navigate({ to: item.href })}
          >
            <span className="text-sm">{item.label}</span>
            <span className="flex items-center gap-2">
              <span className="font-semibold tabular-nums text-amber-600 dark:text-amber-400">
                {item.count.toLocaleString("lo-LA")}
              </span>
              <ChevronRight className="size-4 shrink-0 text-muted-foreground" />
            </span>
          </button>
        </li>
      ))}
    </ul>
  );
}

export function DashboardInbox() {
  const { has } = usePermissions();

  const canOtApprove = has(Permissions.hr_overtime.approve);
  const canLeaveHr = has(Permissions.hr_leave.approve);
  const canLeaveLine = has(Permissions.hr_leave.approve_line);
  const canSalaryAdvance =
    has(Permissions.hr_salary_advance.approve) ||
    has(Permissions.hr_salary_advance.list);
  const canContracts = has(Permissions.contracts.read);

  const hasAnyInboxPermission =
    canOtApprove || canLeaveHr || canLeaveLine || canSalaryAdvance || canContracts;

  const otSummary = useApprovalSummaryQuery(undefined, undefined, {
    enabled: canOtApprove,
  });
  const leaveHrQuery = useLeaveRequestsQuery(
    { limit: 1, offset: 0, status: "pending_hr" },
    { enabled: canLeaveHr },
  );
  const leaveLineQuery = useLeaveRequestsQuery(
    { limit: 1, offset: 0, status: "pending_line" },
    { enabled: canLeaveLine },
  );
  const salaryPending = useSalaryAdvancesQuery(
    { limit: 1, offset: 0, status: "pending" },
    { enabled: canSalaryAdvance },
  );
  const expiring30 = useExpiringContractsQuery(
    { limit: 1, offset: 0, days: 30, excludeDays: 0 },
    { enabled: canContracts },
  );

  const actionable = useMemo(() => {
    const rows: InboxRow[] = [];

    if (canOtApprove) {
      const count = otSummary.data?.pendingCount ?? 0;
      if (count > 0) {
        rows.push({
          id: "ot",
          label: "ຄຳຂໍ OT ລໍອະນຸມັດ",
          count,
          href: "/app/hr/overtime/approvals",
        });
      }
    }

    if (canLeaveHr) {
      const count = leaveHrQuery.data?.meta.total ?? 0;
      if (count > 0) {
        rows.push({
          id: "leave-hr",
          label: "ຄຳຂໍລາ (ລໍ HR)",
          count,
          href: "/app/hr/leave/approvals",
        });
      }
    }

    if (canLeaveLine) {
      const count = leaveLineQuery.data?.meta.total ?? 0;
      if (count > 0) {
        rows.push({
          id: "leave-line",
          label: "ຄຳຂໍລາ (ລໍຫົວໜ້າ)",
          count,
          href: "/app/hr/leave/approvals",
        });
      }
    }

    if (canSalaryAdvance) {
      const count = salaryPending.data?.meta.total ?? 0;
      if (count > 0) {
        rows.push({
          id: "salary-advance",
          label: "ເບິກເງິນເດືອນລ່ວງໜ້າ (ລໍອະນຸມັດ)",
          count,
          href: "/app/hr/salary-advance",
        });
      }
    }

    if (canContracts) {
      const count = expiring30.data?.meta.total ?? 0;
      if (count > 0) {
        rows.push({
          id: "contracts",
          label: "ສັນຍາຫມົດອາຍຸໃນ 30 ວັນ",
          count,
          href: "/app/contracts/expiring",
        });
      }
    }

    return rows;
  }, [
    canOtApprove,
    canLeaveHr,
    canLeaveLine,
    canSalaryAdvance,
    canContracts,
    otSummary.data?.pendingCount,
    leaveHrQuery.data?.meta.total,
    leaveLineQuery.data?.meta.total,
    salaryPending.data?.meta.total,
    expiring30.data?.meta.total,
  ]);

  const isLoading =
    (canOtApprove && otSummary.isLoading) ||
    (canLeaveHr && leaveHrQuery.isLoading) ||
    (canLeaveLine && leaveLineQuery.isLoading) ||
    (canSalaryAdvance && salaryPending.isLoading) ||
    (canContracts && expiring30.isLoading);

  if (!hasAnyInboxPermission) return null;

  return (
    <DashboardSection
      title="ວຽກຄ້າງ / Inbox"
      description="ລາຍການທີ່ຕ້ອງດຳເນີນການ ຫຼື ຕິດຕາມ"
    >
      {isLoading ? (
        <Card
          className={`border border-border ${dashboardLayout.cardChrome} ${dashboardLayout.cardPad}`}
        >
          <p className="text-muted-foreground text-sm">ກຳລັງໂຫຼດ...</p>
        </Card>
      ) : actionable.length > 0 ? (
        <InboxList items={actionable} />
      ) : (
        <Card
          className={`flex items-center gap-3 border border-border ${dashboardLayout.cardChrome} ${dashboardLayout.cardPad}`}
        >
          <Inbox className="size-5 shrink-0 text-muted-foreground" />
          <p className="text-muted-foreground text-sm">
            ບໍ່ມີວຽກຄ້າງໃນລາຍການທີ່ຕິດຕາມ — ສະບາຍດີ!
          </p>
        </Card>
      )}
    </DashboardSection>
  );
}
